import React from 'react'
import {
  Button,
  Card,
  EditIcon,
  Flex,
  H2,
  Input,
  Line,
  P,
  SelectDropdown,
  Span,
} from '../../lib/components'
import { WithdrawalOpportunityListPresenterProps, WithdrawRequest } from '../../lib/types'
import { SM } from '../../lib/utils/constants'
import { formatStatus, displayDollar, getCentsField } from '../../lib/utils/helpers'
import { withdrawalFilters, withdrawalSort } from '../../lib/utils/message'
import { useBreakpoint } from '../../lib/utils/hooks'
import { useNavigate } from 'react-router-dom'
import { Pagination } from '../../lib/components/Pagination'

const WithdrawalOpportunityListPresenter = ({
  investorProfile,
  searchParams,
  withdrawals,
  currentPage,
  pagination,
  handleSearchParams,
  resetSearchParams,
  handleCurrentPage,
}: WithdrawalOpportunityListPresenterProps) => {
  const { breakpoint } = useBreakpoint()
  const navigate = useNavigate()

  const handleWithdrawalApprovalClick = (
    opportunitySlug: string,
    opportunityId: number | string,
    investorId: number | string,
    withdrawalId: number | string,
  ) => {
    navigate(
      `/opportunity/${opportunitySlug}/${opportunityId}/client/${investorId}/withdrawal/detail/${withdrawalId}`,
    )
  }
  return (
    <Flex variant='column'>
      <H2 text='Redemptions' classes={['m-48 align-center']} />

      {investorProfile && (
        <Card classes={['p-40', 'mb-48']}>
          <P text='User Details' />
          <Flex variant='space-between'>
            <Span variant='bold' text='Name' />
            <Span variant='thin' text={investorProfile.name} />
          </Flex>
          <Flex variant='space-between'>
            <Span variant='bold' text='Email' />
            <Span variant='thin' text={investorProfile.email} />
          </Flex>
          {investorProfile.investorReference && (
            <Flex variant='space-between' dataTestId='investor-withdrawals-list-investor-id'>
              <Span variant='bold' text='Client ID' />
              <Span variant='thin' text={investorProfile.investorReference} />
            </Flex>
          )}
        </Card>
      )}

      <Flex variant='column-center'>
        <div className='toolbar-layout gap-12 mb-32'>
          <Input
            name='search'
            value={searchParams.search ?? ''}
            handleChange={(e) => handleSearchParams({ name: e.target.name, value: e.target.value })}
            placeholder='Search'
          />
          <SelectDropdown
            name='filter'
            id='filterId'
            formId=''
            list={withdrawalFilters}
            value={searchParams.filter}
            placeholder={'Filter'}
            handleChange={(val) => handleSearchParams({ name: 'filter', value: val })}
            dataTestId='investor-withdrawals-filter-list-dropdown'
          />
          <div className='sort-reset'>
            <SelectDropdown
              name='sort'
              id='sortById'
              formId=''
              list={withdrawalSort}
              value={searchParams.sort}
              placeholder={'Sort'}
              handleChange={(val) => handleSearchParams({ name: 'sort', value: val })}
              dataTestId='investor-withdrawals-sort-list-dropdown'
            />
            <Button
              handleClick={() => resetSearchParams()}
              classes={['reset-btn']}
              variant='secondary-btn'
              text='Reset'
            />
          </div>
        </div>
        <Line />
        <div className='w-100'>
          {withdrawals && withdrawals.length > 0 ? (
            <Flex
              variant='row'
              classes={['mb-24', 'table_scroll_horizontal']}
              dataTestId='investor-withdrawals-list-table-container'
            >
              <table className={'styled-table'}>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Opportunity Name</th>
                    <th>Opportunity Asset Class</th>
                    <th>Redemption Request Status</th>
                    <th>Requested amount</th>
                    <th>Actual amount sent</th>
                    <th>Redemption Approval</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map(
                    ({
                      id,
                      order_id,
                      opportunity_slug,
                      opportunity_id,
                      investor_id,
                      asset_class,
                      status,
                      amount,
                      currency_code,
                      opportunity_title,
                      ...withdrawal
                    }: WithdrawRequest) => {
                      const actualAmountSent = getCentsField(
                        withdrawal,
                        'actualAmountSent',
                        'actual_amount_sent',
                      )
                      return (
                        <tr key={id}>
                          <td>{order_id}</td>
                          <td>{opportunity_title}</td>
                          <td>{asset_class}</td>
                          <td>{formatStatus(status)}</td>
                          <td data-testid='investor-withdrawals-table-withdrawal-amount-cell'>
                            {displayDollar(amount?.toString(), currency_code, 2, true)}
                          </td>
                          <td data-testid='investor-withdrawals-table-actual-amount-sent-cell'>
                            {actualAmountSent !== undefined
                              ? displayDollar(String(actualAmountSent), currency_code, 2, true)
                              : '—'}
                          </td>
                          <td>
                            <EditIcon
                              classes={['align-center', 'pointer']}
                              height='24px'
                              width='24px'
                              onClick={async () => {
                                handleWithdrawalApprovalClick(
                                  opportunity_slug,
                                  opportunity_id,
                                  investor_id,
                                  id,
                                )
                              }}
                              dataTestId='investor-withdrawals-table-withdrawal-approval-icon'
                            />
                          </td>
                        </tr>
                      )
                    },
                  )}
                </tbody>
              </table>
            </Flex>
          ) : (
            <Flex classes={['column-center']}>
              <Span variant={'large'} text='No Redemption Requests Found' />
              <Span variant={'thin'} text='Try resetting the filters and try again' />
            </Flex>
          )}
        </div>
        {breakpoint && breakpoint === SM
          ? null
          : withdrawals &&
            withdrawals.length > 0 && (
              <Pagination
                currentPage={currentPage}
                handleCurrentPage={handleCurrentPage}
                pagination={pagination}
              />
            )}
      </Flex>
    </Flex>
  )
}

export { WithdrawalOpportunityListPresenter }
